// @ts-nocheck
import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';
import {
  BOOKING_STEPS,
  STORAGE_KEYS,
  URGENCY_VARIANT_CONFIG,
  CHECKMARK_SVG,
  BookingStateManager,
  CustomerDataManager,
  ProductDataManager,
  BookingButtonManager,
  isServiceFeeEnabled,
  shouldSendServiceFeeCode,
  formatEmiratesId
} from '@theme/booking-state';

// Re-export for backward compatibility with other modules that may import from here
export { BOOKING_STEPS, URGENCY_VARIANT_CONFIG, BookingStateManager };

// Local aliases for cleaner code
const BOOKING_STORAGE_KEY = STORAGE_KEYS.BOOKING_DATA;
const PRODUCT_STORAGE_KEY = STORAGE_KEYS.PRODUCT_DATA;
const CUSTOMER_STORAGE_KEY = STORAGE_KEYS.CUSTOMER_DATA;

// Note: URGENCY_VARIANT_CONFIG and BookingStateManager are now imported from booking-state.js
// All state management methods are in the shared module

class CartBookingJourney extends Component {
  constructor() {
    super();
    this.initState();
    // Store bound handler refs once — reused across setupEventListeners calls
    this._handleNextStep = this.handleNextStep.bind(this);
    this._handlePreviousStep = this.handlePreviousStep.bind(this);
    this._handleRadioChange = this.handleRadioChange.bind(this);
    this._handleIdTypeChange = this.handleIdTypeChange.bind(this);
    this._handleNotSureIdTypeChange = this.handleNotSureIdTypeChange.bind(this);
    this._handleSwitchToNewApplication = this.handleSwitchToNewApplication.bind(this);
  }

  initElements() {
    this.nextButton = this.querySelector('[data-next-step]');
    this.previousButton = this.querySelector('[data-previous-step]');
    this.applicationNumberInput = this.querySelector('#application-number');
    this.lastNameInput = this.querySelector('#last-name');
    this.lastNameError = this.querySelector('#last-name-error');
    this.apiError = this.querySelector('#api-error');
    this.productDetails = document.querySelector('.product-details');
    
    // Not-sure form elements
    this.notSureLastNameInput = this.querySelector('#not-sure-last-name');
    this.notSureLastNameError = this.querySelector('#not-sure-last-name-error');
    this.notSurePassportInput = this.querySelector('#not-sure-passport');
    this.notSureEmiratesIdInput = this.querySelector('#not-sure-emirates-id');
    this.notSurePassportField = this.querySelector('#not-sure-passport-field');
    this.notSureEmiratesIdField = this.querySelector('#not-sure-emirates-id-field');
    this.notSureApiError = this.querySelector('#not-sure-api-error');
    
    // Error message elements
    this.applicationNumberError = this.querySelector('#application-number-error');
    this.notSurePassportError = this.querySelector('#not-sure-passport-error');
    this.notSureEmiratesIdError = this.querySelector('#not-sure-emirates-id-error');
    this.newPassportError = this.querySelector('#new-passport-error');
    this.newEmiratesIdError = this.querySelector('#new-emirates-id-error');
    this.newEmailError = this.querySelector('#new-email-error');
  }

  initState() {
    const state = BookingStateManager.getState() || {};
    this.currentPanel = state.panel || 1;
    this.selectedOption = state.applicationType || 'existing';
  }
  
  /**
   * Sync the radio button selection to match the internal selectedOption state
   */
  syncRadioToState() {
    const radio = this.querySelector(`input[name="application-type"][value="${this.selectedOption}"]`);
    if (radio && !radio.checked) {
      radio.checked = true;
    }
  }

  connectedCallback() {
    // AbortController manages all event listener lifecycle — abort() removes them all
    this._abortController = new AbortController();
    const { signal } = this._abortController;

    // Initialize elements now that DOM is ready
    this.initElements();

    // Sync radio button to match selectedOption
    this.syncRadioToState();

    // Clear booking data if cart is empty on page load
    BookingStateManager.clearIfCartEmpty();
    this.setupEventListeners(signal);
    this.loadProductFromLocalStorage();
    this.restoreCustomerData();
    this.phoneIti = null; // intl-tel-input instance
    // Normalize stale state (e.g., step:1 from a prior session) before updating DOM
    BookingStateManager.normalizeStateOnRestore();
    // Restore step number from localStorage
    BookingStateManager.updateDOM();
    this.manageBookingStates();
    setTimeout(() => {
      BookingButtonManager.updateState();
    }, 100);

    // Handle bfcache restore — tear down old listeners and re-register fresh.
    // Stored as named ref and NOT registered with { signal } — it must survive
    // abort() so it can fire on repeat back/forward navigations.
    this._handlePageShow = (event) => {
      if (event.persisted) {
        this._abortController.abort();
        this._abortController = new AbortController();
        const { signal } = this._abortController;

        this.initElements();
        this.initState();
        this.setupEventListeners(signal);
        BookingStateManager.clearIfCartEmpty();
        BookingStateManager.updateDOM();
        this.manageBookingStates();
      }
    };
    window.addEventListener('pageshow', this._handlePageShow);
  }

  disconnectedCallback() {
    this._abortController?.abort();
    window.removeEventListener('pageshow', this._handlePageShow);
  }

  /**
   * Normalize state on restore - if on Step 2 with Panel 1/2, reset to Panel 0
   * Called by cart-drawer when opening to ensure users see main cart
   */
  normalizeStateOnRestore() {
    BookingStateManager.normalizeStateOnRestore();
  }

  /**
   * Initialize intl-tel-input on the phone field
   */
  initPhoneInput() {
    const phoneInput = this.querySelector('#new-phone');
    if (!phoneInput) return;
    
    // Check if already initialized on this specific input
    if (this.phoneIti && this.phoneIti.telInput === phoneInput) return;
    
    // Destroy existing instance if input element changed
    if (this.phoneIti) {
      this.phoneIti.destroy();
      this.phoneIti = null;
    }
    
    // Wait for intlTelInput to be available
    const initIti = () => {
      if (window.intlTelInput) {
        this.phoneIti = window.intlTelInput(phoneInput, {
          initialCountry: 'ae',
          preferredCountries: ['ae', 'sa', 'kw', 'bh', 'om', 'qa'],
          separateDialCode: true,
          useFullscreenPopup: false,
          utilsScript: 'https://cdn.jsdelivr.net/npm/intl-tel-input@24.5.0/build/js/utils.js',
        });
        
        // Scroll input into view when focused on mobile (above keyboard)
        phoneInput.addEventListener('focus', () => {
          setTimeout(() => {
            phoneInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 300);
        });
      } else {
        // Retry after a short delay if library not loaded yet
        setTimeout(initIti, 100);
      }
    };
    initIti();
  }

  /**
   * Get the full phone number with country code from intl-tel-input
   * @returns {string} Full phone number in E.164 format
   */
  getFullPhoneNumber() {
    if (this.phoneIti) {
      return this.phoneIti.getNumber();
    }
    const phoneInput = this.querySelector('#new-phone');
    return phoneInput?.value || '';
  }

  setupEventListeners(signal) {
    const opts = { signal };

    const radioButtons = this.querySelectorAll('input[name="application-type"]');
    radioButtons.forEach(radio => {
      radio.addEventListener('change', this._handleRadioChange, opts);
    });

    if (this.nextButton) {
      this.nextButton.addEventListener('click', this._handleNextStep, opts);
    }
    if (this.previousButton) {
      this.previousButton.addEventListener('click', this._handlePreviousStep, opts);
    }

    if (this.applicationNumberInput) {
      this.applicationNumberInput.addEventListener('input', () => this.validateForm(false), opts);
      this.applicationNumberInput.addEventListener('blur', () => this.validateForm(true), opts);
    }
    if (this.lastNameInput) {
      this.lastNameInput.addEventListener('input', () => this.validateForm(false), opts);
      this.lastNameInput.addEventListener('blur', () => this.validateForm(true), opts);
    }

    // New application form event listeners
    const newAppFields = ['#new-first-name', '#new-last-name', '#new-dob', '#new-gender', '#new-nationality', '#new-email', '#new-phone', '#new-passport'];
    newAppFields.forEach(selector => {
      const field = this.querySelector(selector);
      if (field) {
        field.addEventListener('input', () => this.validateNewApplicationForm(false), opts);
        field.addEventListener('blur', () => this.validateNewApplicationForm(true), opts);
      }
    });

    // Emirates ID is wired separately so the mask runs before validation.
    // The blur mask catches values set without an input event (e.g. restored
    // by the browser), which would otherwise display undashed.
    const newEmiratesIdInput = this.querySelector('#new-emirates-id');
    if (newEmiratesIdInput) {
      newEmiratesIdInput.addEventListener('input', (event) => {
        this.maskEmiratesIdInput(newEmiratesIdInput, event.inputType);
        this.validateNewApplicationForm(false);
      }, opts);
      newEmiratesIdInput.addEventListener('blur', () => {
        this.maskEmiratesIdInput(newEmiratesIdInput);
        this.validateNewApplicationForm(true);
      }, opts);
    }

    // ID type radio buttons for new application form
    const idTypeRadios = this.querySelectorAll('input[name="id-type"]');
    idTypeRadios.forEach(radio => {
      radio.addEventListener('change', this._handleIdTypeChange, opts);
    });

    // Not-sure form event listeners
    if (this.notSureLastNameInput) {
      this.notSureLastNameInput.addEventListener('input', () => this.validateNotSureForm(false), opts);
      this.notSureLastNameInput.addEventListener('blur', () => this.validateNotSureForm(true), opts);
    }
    if (this.notSurePassportInput) {
      this.notSurePassportInput.addEventListener('input', () => this.validateNotSureForm(false), opts);
      this.notSurePassportInput.addEventListener('blur', () => this.validateNotSureForm(true), opts);
    }
    if (this.notSureEmiratesIdInput) {
      this.notSureEmiratesIdInput.addEventListener('input', (event) => {
        this.maskEmiratesIdInput(this.notSureEmiratesIdInput, event.inputType);
        this.validateNotSureForm(false);
      }, opts);
      this.notSureEmiratesIdInput.addEventListener('blur', () => {
        this.maskEmiratesIdInput(this.notSureEmiratesIdInput);
        this.validateNotSureForm(true);
      }, opts);
    }

    // ID type radio buttons for not-sure form
    const notSureIdTypeRadios = this.querySelectorAll('input[name="not-sure-id-type"]');
    notSureIdTypeRadios.forEach(radio => {
      radio.addEventListener('change', this._handleNotSureIdTypeChange, opts);
    });

    // "Start a new application" buttons (there are multiple now)
    const startNewAppBtns = this.querySelectorAll('.start-new-applocation');
    startNewAppBtns.forEach(btn => {
      btn.addEventListener('click', this._handleSwitchToNewApplication, opts);
    });
  }

  /**
   * Switch from existing application form to new application form
   */
  handleSwitchToNewApplication(event) {
    event.preventDefault();
    this.selectedOption = 'new';
    this.syncRadioToState(); // Sync radio button to match the new selection
    this.saveBookingData({
      step: BOOKING_STEPS.APPLICATION,
      applicationType: 'new',
      panel: this.currentPanel
    });
    this.manageBookingStates();
  }

  /**
   * Handle ID type radio change (Passport vs Emirates ID)
   */
  handleIdTypeChange(event) {
    const passportField = this.querySelector('#passport-field');
    const emiratesIdField = this.querySelector('#emirates-id-field');
    
    if (event.target.value === 'passport') {
      passportField?.classList.remove('hidden');
      emiratesIdField?.classList.add('hidden');
    } else {
      passportField?.classList.add('hidden');
      emiratesIdField?.classList.remove('hidden');
    }
    
    this.validateNewApplicationForm();
  }

  handleRadioChange(event) {
    this.selectedOption = event.target.value;
    this.saveBookingData({ applicationType: this.selectedOption });
    this.manageBookingStates();
  }

  handleNextStep(event) {
    event.preventDefault();

    const state = BookingStateManager.getState();
    const step = state?.step || BOOKING_STEPS.APPLICATION;
    console.log('[DEBUG handleNextStep]', { step, currentPanel: this.currentPanel, state, nextButton: this.nextButton, isConnected: this.isConnected });

    // Step 1, Panel 1: Go to Panel 2 with selected application type
    if (step === BOOKING_STEPS.APPLICATION && this.currentPanel === 1) {
      this.currentPanel = 2;
      this.saveBookingData({
        step: BOOKING_STEPS.APPLICATION,
        applicationType: this.selectedOption,
        panel: this.currentPanel
      });
      this.manageBookingStates();
      
      // Initialize phone input after panel becomes visible for "new" application
      if (this.selectedOption === 'new') {
        setTimeout(() => this.initPhoneInput(), 100);
      }
      return;
    }

    // Step 1, Panel 2: Handle form submission based on application type
    if (step === BOOKING_STEPS.APPLICATION && this.currentPanel === 2) {
      if (this.selectedOption === 'existing') {
        this.handleSubmit(event);
      } else if (this.selectedOption === 'not-sure') {
        this.handleNotSureSubmit(event);
      } else if (this.selectedOption === 'new') {
        this.handleNewApplicationSubmit(event);
      }
      return;
    }
    
    // Step 1, Panel 3: Confirm booking details
    if (step === BOOKING_STEPS.APPLICATION && this.currentPanel === 3) {
      // For wellness flow, skip add to cart (item already in cart) and go to location
      if (state?.isWellnessFlow) {
        this.handleWellnessConfirm();
      } else {
        // Government flow: add to cart first
        this.handleAddToCart();
      }
      return;
    }
    
    // Default: Move to next panel
    this.currentPanel = this.currentPanel + 1;
    this.saveBookingData({
      step: BOOKING_STEPS.APPLICATION,
      applicationType: this.selectedOption,
      panel: this.currentPanel
    });
    this.manageBookingStates();
  }

  handlePreviousStep(event) {
    event.preventDefault();
    
    // For wellness flow on panel 2, go back to cart view instead of panel 1
    const state = BookingStateManager.getState();
    if (state?.isWellnessFlow && this.currentPanel === 2) {
      // Clear wellness flow state and go back to cart view
      this.isWellnessFlow = false;
      BookingStateManager.setState({
        step: BOOKING_STEPS.CART_SUMMARY,
        panel: 1,
        isWellnessFlow: false
      });
      
      // Remove step/panel attributes to show cart view
      const cartDrawerInner = document.querySelector('.cart-drawer__inner');
      if (cartDrawerInner) {
        cartDrawerInner.removeAttribute('data-step');
        cartDrawerInner.removeAttribute('data-panel');
        cartDrawerInner.removeAttribute('data-application-type');
      }
      return;
    }
    
    this.currentPanel = Math.max(1, this.currentPanel - 1);
    
    this.saveBookingData({
      step: BOOKING_STEPS.APPLICATION,
      applicationType: this.selectedOption,
      panel: this.currentPanel
    });
    this.manageBookingStates();
  }

  loadProductFromLocalStorage() {
    const productData = this.getProductFromLocalStorage();
    if (productData && productData.name) {
      this.updateProductDisplay(productData.name);
    }
  }

  getProductFromLocalStorage() {
    try {
      const data = localStorage.getItem(PRODUCT_STORAGE_KEY);
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null;
    }
  }

  updateProductDisplay(productName) {
    const productNameElement = this.querySelector('[data-product-name]');
    if (productNameElement) {
      productNameElement.textContent = productName;
    }
  }

  saveBookingData(data) {
    return BookingStateManager.setState(data);
  }

  /**
   * Save booking details to cart attributes
   * @param {Object} bookingId - The booking ID object from saveBooking response
   */
  async saveBookingToCartAttributes(bookingId) {
    try {
      // Extract just the ID string from the bookingId object
      const idValue = typeof bookingId === 'object' ? bookingId.id : bookingId;
      
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attributes: {
            'booking-details': idValue
          }
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update cart attributes: ${response.status}`);
      }
      
      const cart = await response.json();
      return cart;
    } catch (error) {
      console.error('Error saving booking to cart attributes:', error);
      throw error;
    }
  }

  getBookingData() {
    return BookingStateManager.getState();
  }

  clearBookingData() {
    BookingStateManager.clearState();
  }

  saveCustomerData(data) {
    localStorage.setItem(CUSTOMER_STORAGE_KEY, JSON.stringify(data));
    
    // Also save applicant name to BookingStateManager for booking summary
    if (data.applicantName) {
      BookingStateManager.setState({ applicantName: data.applicantName });
    }
    
    // Update cart drawer button visibility after saving customer data
    const cartDrawer = document.querySelector('cart-drawer-component');
    // @ts-ignore
    cartDrawer?.updateCustomerDataState?.();
  }

  getCustomerData() {
    try {
      const data = localStorage.getItem(CUSTOMER_STORAGE_KEY);
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Restores customer data from localStorage and populates Panel 3 fields
   */
  restoreCustomerData() {
    const customerData = this.getCustomerData();
    if (!customerData) return;

    const detailsList = this.querySelector('#application-details-list');
    if (!detailsList) return;

    // Populate each field from stored customer data
    Object.keys(customerData).forEach(fieldName => {
      const valueElement = detailsList.querySelector(`[data-field="${fieldName}"]`);
      if (valueElement) {
        valueElement.textContent = customerData[fieldName];
      }
    });
  }

  manageButtonStates() {
    const state = BookingStateManager.getState();
    if (!state) return;
  }

  /**
   * Updates the DOM and button states based on current booking state
   */
  manageBookingStates() {
    const state = BookingStateManager.getState();
    if (!state) return;

    // Update DOM attributes
    BookingStateManager.updateDOM();

    // Update button states for Step 1 (Application flow)
    if (state.step === BOOKING_STEPS.APPLICATION) {
      this.updateStep1ButtonState(state.panel);
      
      // Initialize phone input when on Panel 2 with "new" application type
      if (state.panel === 2 && state.applicationType === 'new') {
        setTimeout(() => this.initPhoneInput(), 100);
      }
    }
  }

  /**
   * Updates button text and state for Step 1 panels
   */
  updateStep1ButtonState(panel) {
    if (!this.nextButton) return;

    switch (panel) {
      case 1:
        this.nextButton.innerText = 'Next step';
        this.nextButton.disabled = false;
        this.previousButton.classList.add('hidden');
        break;
      case 2:
        this.nextButton.innerText = 'Submit';
        this.nextButton.disabled = true;
        this.previousButton.classList.remove('hidden');
        // Validate based on application type
        if (this.selectedOption === 'existing') {
          this.validateForm();
        } else if (this.selectedOption === 'not-sure') {
          this.validateNotSureForm();
        } else if (this.selectedOption === 'new') {
          this.applyHouseholdGenderRestriction();
          this.validateNewApplicationForm();
        }
        break;
      case 3:
        this.nextButton.innerText = 'Confirm';
        this.nextButton.disabled = false;
        this.previousButton.classList.remove('hidden');
        break;
    }
  }

  /**
   * For household products, restrict gender selection based on variant title
   * If variant title contains "Male" or "Female", disable the non-matching option
   */
  applyHouseholdGenderRestriction() {
    const genderSelect = this.querySelector('#new-gender');
    if (!genderSelect) return;

    // Get product data from localStorage
    const productData = this.getProductFromLocalStorage();
    if (!productData) return;

    // Check if this is a household product (isHDS)
    const config = URGENCY_VARIANT_CONFIG[productData.productId];
    if (!config || !config.isHDS) {
      // Not a household product, enable all options
      Array.from(genderSelect.options).forEach(option => {
        option.disabled = false;
      });
      return;
    }

    // Extract gender from variant title (e.g., "Male" or "Female")
    const variantTitle = productData.variantTitle || '';
    const variantGender = variantTitle.includes('Male') ? 'Male' : 
                          variantTitle.includes('Female') ? 'Female' : null;

    if (!variantGender) {
      // No gender in variant title, enable all options
      Array.from(genderSelect.options).forEach(option => {
        option.disabled = false;
      });
      return;
    }

    // Disable non-matching gender and select the matching one
    Array.from(genderSelect.options).forEach(option => {
      if (option.value && option.value !== variantGender) {
        option.disabled = true;
      } else {
        option.disabled = false;
      }
    });

    // Auto-select the matching gender
    genderSelect.value = variantGender;
  }

  validateForm(showErrors = true) {
    if (!this.applicationNumberInput || !this.lastNameInput) return;
    
    const applicationNumber = this.applicationNumberInput.value.trim();
    const lastName = this.lastNameInput.value.trim();
    
    // Validate last name
    const isLastNameValid = this.validateLastName(lastName, showErrors);
    
    // Validate application number
    const isApplicationNumberValid = this.validateApplicationNumber(applicationNumber, showErrors);
    
    // Enable submit button if both fields are filled and valid
    if (applicationNumber && lastName && isLastNameValid && isApplicationNumberValid) {
      this.nextButton.disabled = false;
    } else {
      this.nextButton.disabled = true;
    }
  }

  /**
   * Validate application number - must start with 7 and be 12 digits
   * @param {string} applicationNumber - The application number to validate
   * @param {boolean} showErrors - Whether to show error messages (true on blur, false on input)
   */
  validateApplicationNumber(applicationNumber, showErrors = true) {
    if (!this.applicationNumberInput || !this.applicationNumberError) return true;
    
    // Clear previous error state
    this.applicationNumberInput.classList.remove('error');
    this.applicationNumberError.classList.remove('show');
    this.applicationNumberError.textContent = '';
    
    if (!applicationNumber) {
      return false;
    }
    
    // Must start with 7 and be exactly 12 digits
    const regex = /^7\d{11}$/;
    if (!regex.test(applicationNumber)) {
      if (showErrors) {
        this.applicationNumberInput.classList.add('error');
        this.applicationNumberError.textContent = Theme.translations?.booking_journey?.application_number_invalid || 'Please enter a valid Application Number';
        this.applicationNumberError.classList.add('show');
      }
      return false;
    }
    
    return true;
  }

  /**
   * Validate passport - min 7, max 14 characters
   * @param {string} passport - The passport number to validate
   * @param {HTMLElement} inputElement - The input element
   * @param {HTMLElement} errorElement - The error message element
   * @param {boolean} showErrors - Whether to show error messages (true on blur, false on input)
   */
  validatePassport(passport, inputElement, errorElement, showErrors = true) {
    if (!inputElement || !errorElement) return true;
    
    // Clear previous error state
    inputElement.classList.remove('error');
    errorElement.classList.remove('show');
    errorElement.textContent = '';
    
    if (!passport) {
      return false;
    }
    
    // Must be 7-14 characters
    if (passport.length < 7 || passport.length > 14) {
      if (showErrors) {
        inputElement.classList.add('error');
        errorElement.textContent = Theme.translations?.booking_journey?.passport_invalid || 'Passport must be 7-14 characters';
        errorElement.classList.add('show');
      }
      return false;
    }
    
    return true;
  }

  /**
   * Apply the Emirates ID mask to an input as the user types, keeping the
   * caret in the same place relative to the digits they have entered.
   * @param {HTMLInputElement} input - The Emirates ID input
   * @param {string} [inputType] - The InputEvent's inputType; omit outside input events
   */
  maskEmiratesIdInput(input, inputType) {
    if (!input) return;

    const caretStart = input.selectionStart ?? input.value.length;
    const digitsBeforeCaret = input.value.slice(0, caretStart).replace(/\D/g, '').length;
    const formatted = formatEmiratesId(input.value);

    if (formatted === input.value) return;

    input.value = formatted;

    // Walk the formatted value until the same number of digits has been passed
    let caret = digitsBeforeCaret === 0 ? 0 : formatted.length;
    let seen = 0;
    for (let i = 0; i < formatted.length; i++) {
      if (formatted[i] !== '-') {
        seen++;
        if (seen === digitsBeforeCaret) {
          caret = i + 1;
          break;
        }
      }
    }
    // After typing a digit that completes a group, hop over the new dash.
    // Not on backspace: when a deleted dash is restored the caret must land
    // before it, so the next backspace reaches the digit and the user is not
    // trapped at the group boundary.
    if (formatted[caret] === '-' && inputType !== 'deleteContentBackward') caret++;

    input.setSelectionRange(caret, caret);
  }

  /**
   * Validate Emirates ID - format 784-xxxx-xxxxxxx-x (dashes optional)
   * @param {string} emiratesId - The Emirates ID to validate
   * @param {HTMLElement} inputElement - The input element
   * @param {HTMLElement} errorElement - The error message element
   * @param {boolean} showErrors - Whether to show error messages (true on blur, false on input)
   */
  validateEmiratesId(emiratesId, inputElement, errorElement, showErrors = true) {
    if (!inputElement || !errorElement) return true;
    
    // Clear previous error state
    inputElement.classList.remove('error');
    errorElement.classList.remove('show');
    errorElement.textContent = '';
    
    if (!emiratesId) {
      return false;
    }
    
    // Format: 784-XXXX-XXXXXXX-X - must start with 784, dashes optional
    const emiratesIdRegex = /^784-?[0-9]{4}-?[0-9]{7}-?[0-9]{1}$/;

    if (!emiratesIdRegex.test(emiratesId)) {
      if (showErrors) {
        inputElement.classList.add('error');
        errorElement.textContent = Theme.translations?.booking_journey?.emirates_id_invalid || 'Emirates ID format: 784-XXXX-XXXXXXX-X';
        errorElement.classList.add('show');
      }
      return false;
    }
    
    return true;
  }

  /**
   * Validate email - simple format xxx@xxx.xxx
   * @param {string} email - The email to validate
   * @param {HTMLElement} inputElement - The input element
   * @param {HTMLElement} errorElement - The error message element
   * @param {boolean} showErrors - Whether to show error messages (true on blur, false on input)
   */
  validateEmail(email, inputElement, errorElement, showErrors = true) {
    if (!inputElement || !errorElement) return true;
    
    // Clear previous error state
    inputElement.classList.remove('error');
    errorElement.classList.remove('show');
    errorElement.textContent = '';
    
    if (!email) {
      return false;
    }
    
    // Simple email format: xxx@xxx.xxx
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!regex.test(email)) {
      if (showErrors) {
        inputElement.classList.add('error');
        errorElement.textContent = Theme.translations?.booking_journey?.email_invalid || 'Please enter a valid email address';
        errorElement.classList.add('show');
      }
      return false;
    }
    
    return true;
  }

  /**
   * Validate last name
   * @param {string} lastName - The last name to validate
   * @param {boolean} showErrors - Whether to show error messages (true on blur, false on input)
   */
  validateLastName(lastName, showErrors = true) {
    if (!this.lastNameInput || !this.lastNameError) return true;
    
    // Clear previous error state
    this.lastNameInput.classList.remove('error');
    this.lastNameError.classList.remove('show');
    this.lastNameError.textContent = '';
    
    // Check if empty (handled by required attribute, but we check for better UX)
    if (!lastName) {
      return false;
    }
    
    // Check minimum length (at least 2 characters)
    if (lastName.length < 2) {
      if (showErrors) {
        this.lastNameInput.classList.add('error');
        this.lastNameError.textContent = Theme.translations?.booking?.last_name_min_length || 'Last Name must be at least 2 characters';
        this.lastNameError.classList.add('show');
      }
      return false;
    }
    
    // Check for numbers (no digits allowed)
    if (/\d/.test(lastName)) {
      if (showErrors) {
        this.lastNameInput.classList.add('error');
        this.lastNameError.textContent = Theme.translations?.booking?.last_name_no_numbers || 'Last name cannot contain numbers';
        this.lastNameError.classList.add('show');
      }
      return false;
    }
    
    return true;
  }

  async handleSubmit(event) {
    event.preventDefault();
    
    const applicationNumber = this.applicationNumberInput.value.trim();
    const lastName = this.lastNameInput.value.trim();
    
    // Validate before submission
    if (!applicationNumber || !lastName || !this.validateLastName(lastName)) {
      console.error('Form validation failed');
      return;
    }
    
    // Clear any previous API errors
    this.clearApiError();
    
    // Show loading state
    const originalText = this.nextButton.innerHTML;
    this.nextButton.disabled = true;
    this.nextButton.innerHTML = `<span class="spinner"></span> ${Theme.translations?.booking?.submitting || 'Submitting...'}`;
    
    try {
      // Get the booking-journey component to call its API method
      const bookingJourney = document.querySelector('booking-journey');
      if (bookingJourney && typeof bookingJourney.applicationDetails === 'function') {
        const result = await bookingJourney.applicationDetails(applicationNumber, lastName);
        
        // Check if the API returned success
        if (result && result.status == 200) {
          
          // Save customer data to localStorage
          this.saveCustomerData(result.data || result);
          
          // Populate the application details in panel 3
          this.populateApplicationDetails(result.data || result);
          
          // Move to next panel
          this.currentPanel = this.currentPanel + 1;
          this.saveBookingData({
            step: BOOKING_STEPS.APPLICATION,
            applicationType: this.selectedOption,
            panel: this.currentPanel,
            applicationNumber: applicationNumber,
            lastName: lastName
          });

          this.manageBookingStates();
          return; // Exit early on success - button state already updated by manageBookingStates
        } else {
          if(result.status == 500){

          const closeButton = this.querySelector('button.close-button');
          if (closeButton) {
            closeButton.click();
          }

          this.showBookingErrorDialog(
            Theme.translations?.booking?.application_already_processed_title || `We've already processed this application`,
            Theme.translations?.booking?.application_already_processed_message || `Please review your booking and select a different service.`
          );
            return;
          } else if(result.status == 404){
            console.error('Error submitting application details:', result);
            this.showApiError(Theme.translations?.booking?.application_not_found || `Check application number or start a new application.`);
          } else {
          // API call did not return success
          this.showApiError(Theme.translations?.booking?.application_not_found || `Check application number or start a new application`);
          }
        }
      } else {
        console.error('BookingJourney component not found or method not available');
        this.showApiError(Theme.translations?.booking?.general_error || `Sorry, something went wrong. Please try again or contact us on +971 4260 4900.`);
      }
    } catch (error) {
        console.error('Error submitting application details:', error, error.status);
        this.showApiError(Theme.translations?.booking?.details_mismatch || `Details you've provided don't match Government records. Verify and try again.`);
    }
    // Restore button state only on error (success returns early)
    this.nextButton.innerHTML = originalText;
    this.nextButton.disabled = false;
  }

  showApiError(message) {
    if (this.apiError) {
      this.apiError.innerHTML = message;
      this.apiError.classList.add('show');
    }
  }

  clearApiError() {
    if (this.apiError) {
      this.apiError.innerHTML = '';
      this.apiError.classList.remove('show');
    }
  }

  /**
   * Show a booking error dialog with custom title and message
   * Reuses the native dialog element from cart-drawer.liquid
   */
  showBookingErrorDialog(title, message) {
    const dialog = document.querySelector('[data-booking-error-dialog]');
    if (!dialog) {
      console.error('Booking error dialog not found');
      return;
    }

    const titleEl = dialog.querySelector('[data-error-title]');
    const messageEl = dialog.querySelector('[data-error-message]');
    const closeBtn = dialog.querySelector('[data-error-close]');

    if (titleEl) titleEl.innerHTML = title;
    if (messageEl) messageEl.innerHTML = message;

    // Setup close button handler
    const handleClose = () => {
      dialog.close();
      closeBtn?.removeEventListener('click', handleClose);
    };
    closeBtn?.addEventListener('click', handleClose);

    dialog.showModal();
  }

  /**
   * Handle ID type radio change for not-sure form (Passport vs Emirates ID)
   */
  handleNotSureIdTypeChange(event) {
    if (event.target.value === 'passport') {
      this.notSurePassportField?.classList.remove('hidden');
      this.notSureEmiratesIdField?.classList.add('hidden');
    } else {
      this.notSurePassportField?.classList.add('hidden');
      this.notSureEmiratesIdField?.classList.remove('hidden');
    }
    
    this.validateNotSureForm();
  }

  /**
   * Validate the not-sure form (Last Name + Passport/Emirates ID)
   * @param {boolean} showErrors - Whether to show error messages (true on blur, false on input)
   */
  validateNotSureForm(showErrors = true) {
    const lastName = this.notSureLastNameInput?.value?.trim();
    
    // Check ID type and corresponding field
    const idType = this.querySelector('input[name="not-sure-id-type"]:checked')?.value;
    const passport = this.notSurePassportInput?.value?.trim();
    const emiratesId = this.notSureEmiratesIdInput?.value?.trim();
    
    // Validate last name (letters only, at least 2 characters)
    const isLastNameValid = lastName && lastName.length >= 2 && /^[a-zA-Z\s'-]+$/.test(lastName);
    
    // Show/hide last name error
    if (this.notSureLastNameError) {
      if (showErrors && lastName && !isLastNameValid) {
        this.notSureLastNameInput?.classList.add('error');
        this.notSureLastNameError.textContent = Theme.translations?.booking?.last_name_invalid || 'Please enter a valid last name (letters only)';
        this.notSureLastNameError.classList.add('show');
      } else {
        this.notSureLastNameInput?.classList.remove('error');
        this.notSureLastNameError.textContent = '';
        this.notSureLastNameError.classList.remove('show');
      }
    }
    
    // Validate passport or emirates ID based on selection
    let isIdValid = false;
    if (idType === 'passport') {
      isIdValid = this.validatePassport(passport, this.notSurePassportInput, this.notSurePassportError, showErrors);
    } else {
      isIdValid = this.validateEmiratesId(emiratesId, this.notSureEmiratesIdInput, this.notSureEmiratesIdError, showErrors);
    }
    
    const isValid = isLastNameValid && isIdValid;
    
    if (this.nextButton) {
      this.nextButton.disabled = !isValid;
    }
    
    return isValid;
  }

  /**
   * Show API error for not-sure form
   */
  showNotSureApiError(message) {
    if (this.notSureApiError) {
      this.notSureApiError.innerHTML = message;
      this.notSureApiError.classList.add('show');
    }
  }

  /**
   * Clear API error for not-sure form
   */
  clearNotSureApiError() {
    if (this.notSureApiError) {
      this.notSureApiError.innerHTML = '';
      this.notSureApiError.classList.remove('show');
    }
  }

  /**
   * Handle not-sure form submission - uses mfDetails API
   */
  async handleNotSureSubmit(event) {
    event.preventDefault();
    
    if (!this.validateNotSureForm()) {
      return;
    }
    
    const lastName = this.notSureLastNameInput?.value?.trim();
    const idType = this.querySelector('input[name="not-sure-id-type"]:checked')?.value;
    const passport = idType === 'passport' ? this.notSurePassportInput?.value?.trim() : '';
    // API expects the dashed format, so normalize rather than send the raw value
    const emiratesId = idType === 'emirates-id' ? formatEmiratesId(this.notSureEmiratesIdInput?.value?.trim()) : '';
    
    // Clear any previous API errors
    this.clearNotSureApiError();
    
    // Show loading state
    const originalText = this.nextButton.innerHTML;
    this.nextButton.disabled = true;
    this.nextButton.innerHTML = `<span class="spinner"></span> ${Theme.translations?.booking?.searching || 'Searching...'}`;
    
    try {
      // Get the booking-journey component to call its API method
      const bookingJourney = document.querySelector('booking-journey');
      
      if (bookingJourney && typeof bookingJourney.mfDetails === 'function') {
        // Step 1: Call mfDetails to get applicationNumber
        const mfResult = await bookingJourney.mfDetails(lastName, emiratesId, passport);
        
        // Check if we got an applicationNumber
        if (mfResult && mfResult.applicationNumber) {
          // Step 2: Call applicationDetails with the applicationNumber and lastName
          // Pass true for isAlreadyEncoded since mfDetails returns base64 encoded applicationNumber
          const appDetails = await bookingJourney.applicationDetails(mfResult.applicationNumber, lastName, true);
          
          if (appDetails && appDetails.status === 200) {
            // Save customer data from applicationDetails
            this.saveCustomerData(appDetails.data || appDetails);
            
            // Populate the application details in panel 3
            this.populateApplicationDetails(appDetails.data || appDetails);
            
            // Keep selectedOption as 'not-sure' so Previous button returns to correct panel
            // this.selectedOption stays as 'not-sure'
            
            // Move to next panel (confirmation)
            this.currentPanel = this.currentPanel + 1;
            this.saveBookingData({
              step: BOOKING_STEPS.APPLICATION,
              applicationType: 'not-sure',
              panel: this.currentPanel,
              lastName: lastName
            });

            this.manageBookingStates();
            return; // Exit early on success - button state already updated by manageBookingStates
          } else {
            this.showNotSureApiError('Could not retrieve application details');
          }
        } else {
          // API call did not return applicationNumber
          this.showNotSureApiError(mfResult?.message || 'Could not find application with these details');
        }
      } else {
        console.error('BookingJourney component not found or mfDetails method not available');
        this.showNotSureApiError('Could not find application 1');
      }
    } catch (error) {
      console.error('Error searching for application:', error);
      this.showNotSureApiError(Theme.translations?.booking?.details_mismatch || ``);
    }
    // Restore button state only on error (success returns early)
    this.nextButton.innerHTML = originalText;
    this.nextButton.disabled = false;
  }

  /**
   * Validate the new application form (Panel 4)
   * @param {boolean} showErrors - Whether to show error messages (true on blur, false on input)
   */
  validateNewApplicationForm(showErrors = true) {
    const firstName = this.querySelector('#new-first-name')?.value?.trim();
    const lastName = this.querySelector('#new-last-name')?.value?.trim();
    const dob = this.querySelector('#new-dob')?.value?.trim();
    const gender = this.querySelector('#new-gender')?.value;
    const nationality = this.querySelector('#new-nationality')?.value?.trim();
    const email = this.querySelector('#new-email')?.value?.trim();
    const emailInput = this.querySelector('#new-email');
    const phone = this.querySelector('#new-phone')?.value?.trim();
    
    // Check ID type and corresponding field
    const idType = this.querySelector('input[name="id-type"]:checked')?.value;
    const passport = this.querySelector('#new-passport')?.value?.trim();
    const emiratesId = this.querySelector('#new-emirates-id')?.value?.trim();
    const passportInput = this.querySelector('#new-passport');
    const emiratesIdInput = this.querySelector('#new-emirates-id');
    
    // Validate passport or emirates ID based on selection
    let isIdValid = false;
    if (idType === 'passport') {
      isIdValid = this.validatePassport(passport, passportInput, this.newPassportError, showErrors);
    } else {
      isIdValid = this.validateEmiratesId(emiratesId, emiratesIdInput, this.newEmiratesIdError, showErrors);
    }
    
    // Validate email
    const isEmailValid = this.validateEmail(email, emailInput, this.newEmailError, showErrors);
    
    // All required fields must be filled
    const isValid = firstName && lastName && dob && gender && nationality && phone && isIdValid && isEmailValid;
    
    if (this.nextButton) {
      this.nextButton.disabled = !isValid;
    }
    
    return isValid;
  }

  /**
   * Handle new application form submission (Panel 2, new type)
   */
  handleNewApplicationSubmit(event) {
    event.preventDefault();
    
    if (!this.validateNewApplicationForm()) {
      return;
    }
    
    // Collect form data
    const firstName = this.querySelector('#new-first-name')?.value?.trim();
    const lastName = this.querySelector('#new-last-name')?.value?.trim();
    const dob = this.querySelector('#new-dob')?.value?.trim();
    const gender = this.querySelector('#new-gender')?.value;
    const nationalitySelect = this.querySelector('#new-nationality');
    const nationalityCode = nationalitySelect?.value?.trim();
    const nationalityName = nationalitySelect?.options[nationalitySelect.selectedIndex]?.text || '';
    const email = this.querySelector('#new-email')?.value?.trim();
    const phone = this.getFullPhoneNumber() || this.querySelector('#new-phone')?.value?.trim();
    
    const idType = this.querySelector('input[name="id-type"]:checked')?.value;
    const passport = this.querySelector('#new-passport')?.value?.trim();
    // API expects the dashed format, so normalize rather than send the raw value
    const emiratesId = formatEmiratesId(this.querySelector('#new-emirates-id')?.value?.trim());

    // Create customer data object (same structure as API response)
    // Note: New applicants don't have an application number yet
    const customerData = {
      applicantName: `${firstName} ${lastName}`,
      applicationNumber: '',
      nationality: nationalityName,
      nationalityCode: nationalityCode,
      gender: gender,
      passportNumber: idType === 'passport' ? passport : '',
      emirateIDNumber: idType === 'emirates-id' ? emiratesId : '',
      applicantEmail: email,
      applicantMobile: phone,
      dateOfBirth: dob,
      urgencyCategory: '',
      paidFee: ''
    };
    
    // Populate the application details in panel 3 (isEncoded=false since this is local data)
    // This also saves to localStorage
    this.populateApplicationDetails(customerData, false);
    
    // Move to panel 3 (confirm booking details)
    // Preserve isWellnessFlow flag if set
    const currentState = BookingStateManager.getState();
    this.currentPanel = 3;
    this.saveBookingData({
      step: BOOKING_STEPS.APPLICATION,
      applicationType: this.selectedOption,
      panel: this.currentPanel,
      isNewApplication: true,
      isWellnessFlow: currentState?.isWellnessFlow || false
    });
    
    this.manageBookingStates();
  }

  populateApplicationDetails(data, isEncoded = true) {
    const detailsList = this.querySelector('#application-details-list');
    if (!detailsList || !data) return;

    // Map of field names to their values (decode if from API, use as-is if local)
    const fields = {
      applicantName: data.applicantName || '',
      applicationNumber: isEncoded && data.applicationNumber ? atob(data.applicationNumber) : (data.applicationNumber || ''),
      nationality: data.nationality || '',
      nationalityCode: data.nationalityCode || '',
      gender: data.gender || '',
      dateOfBirth: data.dateOfBirth || '',
      passportNumber: isEncoded && data.passportNumber ? atob(data.passportNumber) : (data.passportNumber || ''),
      emirateIDNumber: isEncoded && data.emirateIDNumber ? atob(data.emirateIDNumber) : (data.emirateIDNumber || ''),
      applicantEmail: isEncoded && data.applicantEmail ? atob(data.applicantEmail) : (data.applicantEmail || ''),
      applicantMobile: isEncoded && data.applicantMobile ? atob(data.applicantMobile) : (data.applicantMobile || ''),
      urgencyCategory: data.urgencyCategory || '-',
      paidFee: data.paidFee !== undefined && data.paidFee !== '' ? `AED ${data.paidFee}` : '-',
      isHDS: data.isHDS || false
    };

    // Save customer data to localStorage for restoration after page refresh (includes nationalityCode for API)
    this.saveCustomerData(fields);

    // Populate each field
    Object.keys(fields).forEach(fieldName => {
      const valueElement = detailsList.querySelector(`[data-field="${fieldName}"]`);
      if (valueElement) {
        valueElement.textContent = fields[fieldName] || '-';
      }
    });
  }

  /**
   * Extract time from urgencyCategory string
   * Returns { value: number, unit: 'hours' | 'mins' } or null if not found
   * Examples: "VIP SERVICE(6HRS)" -> { value: 6, unit: 'hours' }
   *           "VIP SERVICE(30 MINS)" -> { value: 30, unit: 'mins' }
   */
  extractTimeFromUrgency(urgencyCategory) {
    if (!urgencyCategory) return null;
    
    // Match minutes pattern first (e.g., "30 MINS", "30MINS")
    const minsMatch = urgencyCategory.match(/(\d+)\s*MINS?/i);
    if (minsMatch) {
      return { value: parseInt(minsMatch[1], 10), unit: 'mins' };
    }
    
    // Match hours pattern (e.g., "6HRS", "24 HRS")
    const hrsMatch = urgencyCategory.match(/(\d+)\s*HRS?/i);
    if (hrsMatch) {
      return { value: parseInt(hrsMatch[1], 10), unit: 'hours' };
    }
    
    return null;
  }

  /**
   * Get the variant ID to use based on urgencyCategory and product configuration
   * Returns { variantId, skipServiceFee } or null if no upgrade applies
   */
  getUrgencyBasedVariant(productId, apiData) {
    // Check if this product has urgency-based variant configuration
    const config = URGENCY_VARIANT_CONFIG[productId];
    if (!config) return null;

    // Extract time from urgencyCategory (hours or mins)
    const timeInfo = this.extractTimeFromUrgency(apiData.urgencyCategory);
    if (!timeInfo) return null;
    
    const gender = apiData.gender;
    const isHDS = config.isHDS;
    let variantConfig;
    
    if (isHDS) {
      // HDS products match by time and gender
      variantConfig = config.variants.find((v) => {
        if (timeInfo.unit === 'mins') {
          return v.mins === timeInfo.value && v.gender === gender;
        }
        return v.hours === timeInfo.value && v.gender === gender;
      });
    } else {
      // Non-HDS products match by time only
      variantConfig = config.variants.find((v) => {
        if (timeInfo.unit === 'mins') {
          return v.mins === timeInfo.value;
        }
        return v.hours === timeInfo.value;
      });
    }
    
    if (!variantConfig) return null;

    return {
      variantId: variantConfig.variantId,
      skipServiceFee: config.skipServiceFee || false,
      upgradeHandle: config.upgradeHandle,
      isHDS: isHDS
    };
  }

  async handleAddToCart() {
    // Get variant ID from localStorage
    const productData = this.getProductFromLocalStorage();
    if (!productData || !productData.variantId) {
      console.error('No variant ID found in localStorage');
      return;
    }

    // Get customer data to check urgencyCategory
    const customerData = this.getCustomerData();
    const urgencyCategory = customerData?.urgencyCategory || '';
    const isHDS = customerData?.isHDS || false;
    const gender = customerData?.gender || '';
    const apiData = {gender: gender, isHDS: isHDS, urgencyCategory: urgencyCategory};
    
    // Extract product handle from product name (convert to handle format)
    const productID = productData.productId

    // Check if we need to use an urgency-based variant
    const urgencyVariant = this.getUrgencyBasedVariant(productID, apiData);

    // Only check isHDS mismatch for existing applications (not new applicants)
    // 'not-sure' flow also finds existing applications, so include it in this check
    if ((this.selectedOption === 'existing' || this.selectedOption === 'not-sure') && urgencyVariant && isHDS !== urgencyVariant.isHDS) {
      // close cart dialog
      const closeButton = this.querySelector('button.close-button');
      if (closeButton) {
        closeButton.click();
      }
      this.showBookingErrorDialog(
        'You cannot upgrade your application with this service',
        ` ${isHDS == false ? 'Please review your booking and select a Visa Medical Test.' : 'Please review your booking and select a Household Domestic Visa Medical Test.'}`
      );
      console.error('isHDS does not match urgency variant configuration');
      return;
    }
    
    // Determine which variant to add
    const variantId = urgencyVariant?.variantId || productData.variantId;
    // For new applications (no urgencyVariant), always add service fee
    // For existing applications with upgrades, skip service fee based on config
    const skipServiceFee = urgencyVariant?.skipServiceFee || false;

    // Show loading state
    const originalText = this.nextButton.innerHTML;
    this.nextButton.disabled = true;
    this.nextButton.innerHTML = `<span class="spinner"></span> ${Theme.translations?.booking?.adding_to_cart || 'Adding to cart...'}`;

    try {
      // Get cart items section IDs for updating the cart drawer
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      let cartItemComponentsSectionIds = [];
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          cartItemComponentsSectionIds.push(item.dataset.sectionId);
        }
      });

      // Start with the main product variant (with service code as hidden line item property)
      const serviceCode = productData.serviceCode || '';
      const items = [
        { 
          id: variantId, 
          quantity: 1,
          properties: serviceCode ? { '_service_code': serviceCode } : {}
        }
      ];

      // Add service fee only if not skipped (upgrades don't include service fee)
      if (!skipServiceFee) {
        // Record that the fee applies to this booking, independently of whether the
        // line item ends up in the cart. saveBooking() reads this to decide whether
        // to send the 'SF' service code, so the charge can be switched off without
        // changing the API payload.
        this.saveBookingData({ serviceFeeApplies: true });

        // The charge itself is behind a theme setting. cart-drawer.js strips any
        // fee already sitting in the cart when the setting is off.
        if (isServiceFeeEnabled()) {
          try {
            // First check if service fee already exists in cart
            const cartResponse = await fetch('/cart.js');
            let serviceFeeExists = false;

            if (cartResponse.ok) {
              const cart = await cartResponse.json();
              serviceFeeExists = cart.items.some(item =>
                item.properties && item.properties._service_code === 'SF'
              );
            }

            // Only add service fee if it doesn't already exist
            if (!serviceFeeExists) {
              const serviceFeeResponse = await fetch('/products/salem-service-fee.js');
              if (serviceFeeResponse.ok) {
                const serviceFeeProduct = await serviceFeeResponse.json();
                if (serviceFeeProduct.variants && serviceFeeProduct.variants.length > 0) {
                  items.push({
                    id: serviceFeeProduct.variants[0].id,
                    quantity: 1,
                    properties: { '_service_code': 'SF' }
                  });
                }
              }
            } else {
              console.log('Service fee already exists in cart, skipping add');
            }
          } catch (e) {
            console.warn('Could not fetch service fee product:', e);
          }
        }
      } else {
        // Upgrades carry no service fee, so clear the flag as well as the line item.
        // Without this, upgrading after a standard add would leave serviceFeeApplies
        // set and send 'SF' for a booking that was never charged one.
        this.saveBookingData({ serviceFeeApplies: false });

        // If skipServiceFee is true (upgrade variant), remove any existing service fee items from cart
        try {
          // Get current cart
          const cartResponse = await fetch('/cart.js');
          if (cartResponse.ok) {
            const cart = await cartResponse.json();
            
            // Find all service fee items (items with _service_code: 'SF')
            const serviceFeeItems = cart.items.filter(item => 
              item.properties && item.properties._service_code === 'SF'
            );
            
            // Remove each service fee item
            for (const serviceFeeItem of serviceFeeItems) {
              await fetch('/cart/change.js', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json'
                },
                body: JSON.stringify({
                  id: serviceFeeItem.key,
                  quantity: 0
                })
              });
            }
            
            if (serviceFeeItems.length > 0) {
              console.log(`Removed ${serviceFeeItems.length} service fee item(s) from cart`);
            }
          }
        } catch (e) {
          console.warn('Could not remove service fee items:', e);
        }
      }

      // Build request body
      const requestBody = { items };
      if (cartItemComponentsSectionIds.length > 0) {
        requestBody.sections = cartItemComponentsSectionIds.join(',');
      }

      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        body: JSON.stringify(requestBody),
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (result.status) {
        // Error occurred
        console.error('Error adding to cart:', result.message);
        this.nextButton.innerHTML = originalText;
        this.nextButton.disabled = false;
        return;
      }

      // Success - transition to Step 2, Panel 1 (location selection).
      //
      // Panel 0 is the cart view with the popular-services upsell, which used to sit
      // between confirming the booking and choosing a clinic and required the customer
      // to click "Choose Date & Time" to move on. Going straight to Panel 1 removes
      // that step. handleWellnessConfirm() already makes this exact transition
      // automatically, so the pattern is proven.
      this.currentPanel = 1;
      this.saveBookingData({
        step: BOOKING_STEPS.LOCATION,
        panel: this.currentPanel
      });
      
      // Update DOM
      BookingStateManager.updateDOM();
      this.manageBookingStates();

      // Disable all booking journey buttons (govt item now in cart)
      BookingButtonManager.disableAll();
      // setTimeout(() => {
      //   BookingButtonManager.disableAll();
      // }, 5000);

      // Notify other components that booking state has changed (for button visibility)
      document.dispatchEvent(new CustomEvent('booking:stateChanged'));

      // Update cart count
      const cartCount = result.item_count || 1;
      
      // Update sessionStorage for cart count persistence
      sessionStorage.setItem('cart-count', JSON.stringify({
        value: String(cartCount),
        timestamp: Date.now()
      }));

      // Fetch fresh section HTML and dispatch cart:update with sections data
      // This lets component-cart-items handle the selective morph (preserving booking data)
      const cartItemsComponent = document.querySelector('cart-items-component');
      if (cartItemsComponent && cartItemsComponent.dataset.sectionId) {
        const sectionId = cartItemsComponent.dataset.sectionId;
        
        // Show loading overlay while fetching fresh content
        const loadingOverlay = document.createElement('div');
        loadingOverlay.className = 'cart-loading-overlay';
        loadingOverlay.innerHTML = `
          <div class="cart-loading-spinner">
            <span class="spinner"></span>
            <span class="cart-loading-text">Loading your cart...</span>
          </div>
        `;
        cartItemsComponent.style.position = 'relative';
        cartItemsComponent.appendChild(loadingOverlay);

        // The cart view is hidden at Panel 1, so the overlay above is not visible from
        // here. Show the location panel's own spinner as well, otherwise the customer
        // sees the clinic list left over from page load until this refresh lands and
        // could pick a location that is about to disappear.
        const bookingLocation = document.querySelector('cart-booking-location');
        bookingLocation?.showLocationLoading?.();

        try {
          // Fetch fresh section HTML from the current page
          const url = new URL(window.location.href);
          url.searchParams.set('section_id', sectionId);
          
          const response = await fetch(url.toString());
          const html = await response.text();
          
          // Remove loading overlay
          loadingOverlay.remove();
          
          // Dispatch cart:update with sections data - component-cart-items will handle selective morph
          //
          // CartAddEvent.eventName IS 'cart:update' (events.js), so this dispatch also
          // fires the drawer's #handleCartAdd, which under auto-open calls open() →
          // restoreBookingState() → normalizeStateOnRestore() synchronously, and that
          // would reset the Panel 1 state saved above. Guard it exactly the way
          // handleGovtClick guards its own open() call.
          BookingStateManager._skipNormalize = true;
          try {
            document.dispatchEvent(new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                data: {
                  itemCount: cartCount,
                  source: 'cart-booking-journey',
                  sections: { [sectionId]: html }
                }
              }
            }));
          } finally {
            BookingStateManager._skipNormalize = false;
          }
          
          // Also update cart-booking-location with fresh location data
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const newBookingLocation = doc.querySelector('cart-booking-location');
          const currentBookingLocation = document.querySelector('cart-booking-location');
          if (newBookingLocation && currentBookingLocation) {
            currentBookingLocation.innerHTML = newBookingLocation.innerHTML;
            // Re-initialize after content update
            if (typeof currentBookingLocation.reinitialize === 'function') {
              currentBookingLocation.reinitialize();
            }
          }

          // Same tail as the manual "Choose Date & Time" path, which is what this
          // transition replaces: drop the spinner and pick up any pre-selected clinic
          // once the radios exist.
          bookingLocation?.hideLocationLoading?.();
          bookingLocation?.waitForLocationRadiosAndLoad?.();
        } catch (e) {
          console.error('Error refreshing cart content:', e);
          // Remove loading overlay on error, and do not leave the location panel
          // stuck behind its spinner.
          loadingOverlay.remove();
          bookingLocation?.hideLocationLoading?.();
        }
      }

    } catch (error) {
      console.error('Error adding to cart:', error);
      this.nextButton.innerHTML = originalText;
      this.nextButton.disabled = false;
    }
  }

  /**
   * Handle wellness product confirm - skip add to cart and go straight to location
   * Used when item is already in cart (via Finalise Booking button)
   */
  async handleWellnessConfirm() {
    // Clear wellness flow flag
    this.isWellnessFlow = false;
    
    // Transition to Step 2 (Location selection)
    this.currentPanel = 1;
    this.saveBookingData({
      step: BOOKING_STEPS.LOCATION,
      panel: 1,
      isWellnessFlow: false
    });
    
    // Update DOM to show location selection
    BookingStateManager.updateDOM();

    // Notify other components that booking state has changed (for button visibility)
    document.dispatchEvent(new CustomEvent('booking:stateChanged'));
    
    // Update cart drawer button visibility (customer data now exists)
    const cartDrawer = document.querySelector('cart-drawer-component');
    // @ts-ignore
    cartDrawer?.updateCustomerDataState?.();
    
    // Refresh location content from server (locations are rendered via Liquid).
    // Mirror the government path: show the location panel's spinner during the
    // refresh so the customer is not shown (and cannot pick from) the clinic list
    // rendered at page load, then pick up any pre-selected clinic once the fresh
    // radios exist.
    const bookingLocation = document.querySelector('cart-booking-location');
    bookingLocation?.showLocationLoading?.();
    try {
      await this.refreshLocationContent();
    } finally {
      bookingLocation?.hideLocationLoading?.();
    }
    bookingLocation?.waitForLocationRadiosAndLoad?.();
  }
  
  /**
   * Refresh the cart-booking-location content from the server
   * This is needed because locations are rendered server-side via Liquid
   */
  async refreshLocationContent() {
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (!cartItemsComponent || !cartItemsComponent.dataset.sectionId) return;
    
    const sectionId = cartItemsComponent.dataset.sectionId;
    
    try {
      // Fetch fresh section HTML from the current page
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', sectionId);
      
      const response = await fetch(url.toString());
      const html = await response.text();
      
      // Parse and update cart-booking-location with fresh content
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newBookingLocation = doc.querySelector('cart-booking-location');
      const currentBookingLocation = document.querySelector('cart-booking-location');
      
      if (newBookingLocation && currentBookingLocation) {
        currentBookingLocation.innerHTML = newBookingLocation.innerHTML;
        // Re-initialize after content update
        // @ts-ignore
        if (typeof currentBookingLocation.reinitialize === 'function') {
          currentBookingLocation.reinitialize();
        }
      }
    } catch (e) {
      console.error('Error refreshing location content:', e);
    }
  }

}



if (!customElements.get('cart-booking-journey')) {
  customElements.define('cart-booking-journey', CartBookingJourney);
}

/**
 * CartBookingLocation - Handles Step 2 of the booking flow
 * Panel 1: Location selection
 * Panel 2: Date & Time selection
 */
class CartBookingLocation extends Component {
  // Track if change button listeners have been added (prevent duplicates)
  static changeListenersAdded = false;

  constructor() {
    super();
    // Store bound handler refs once
    this._boundHandleNextStep = this.handleNextStep.bind(this);
    this._boundHandlePreviousStep = this.handlePreviousStep.bind(this);
    this._boundUpdateButtonState = this.updateButtonState.bind(this);
  }

  initElements() {
    this.bookingJourney = document.querySelector('cart-booking-journey');
    this.nextButton = this.querySelector('[data-next-step]');
    this.previousButton = this.querySelector('[data-previous-step]');
    this.dateInput = this.querySelector('#selected-date');
    this.timeSelect = this.querySelector('#selected-time');
    this.datePicker = this.querySelector('date-picker-component');
  }

  setupEventListeners(signal) {
    const opts = { signal };

    // Location button (from cart summary) - use event delegation since button may not exist yet
    document.addEventListener('click', (event) => {
      const locationBtn = event.target.closest('[data-booking-location-btn]');
      if (locationBtn) {
        this.handleLocationButtonClick(event);
      }
    }, opts);

    // Navigation buttons
    if (this.nextButton) {
      this.nextButton.addEventListener('click', this._boundHandleNextStep, opts);
    }
    if (this.previousButton) {
      this.previousButton.addEventListener('click', this._boundHandlePreviousStep, opts);
    }

    // Location radio buttons
    const locationRadios = this.querySelectorAll('input[name="location"]');
    locationRadios.forEach(radio => {
      radio.addEventListener('change', this._boundUpdateButtonState, opts);
    });

    // Time select
    if (this.timeSelect) {
      this.timeSelect.addEventListener('change', this._boundUpdateButtonState, opts);
    }

    // Date picker
    if (this.datePicker) {
      this.datePicker.addEventListener('dateselected', (event) => {
        this.handleDateSelected(event);
        this.updateButtonState();
      }, opts);
    }
  }

  connectedCallback() {
    // AbortController manages all event listener lifecycle
    this._abortController = new AbortController();
    const { signal } = this._abortController;

    this.initElements();
    this.setupEventListeners(signal);

    // Setup change button listeners (guarded by static flag — excluded from AbortController)
    this.setupChangeButtonListeners();
    // Restore step number from localStorage
    BookingStateManager.updateDOM();
    // Check initial button state (for preselected locations on refresh)
    this.updateButtonState();

    // Handle bfcache restore — stored ref, NOT registered with { signal }
    // so it survives abort() and fires on repeat back/forward navigations
    this._handlePageShow = (event) => {
      if (event.persisted) {
        this._abortController.abort();
        this._abortController = new AbortController();
        const { signal } = this._abortController;

        this.initElements();
        this.setupEventListeners(signal);
        this.updateButtonState();
      }
    };
    window.addEventListener('pageshow', this._handlePageShow);
  }

  disconnectedCallback() {
    this._abortController?.abort();
    window.removeEventListener('pageshow', this._handlePageShow);
  }

  /**
   * Re-initialize elements and listeners after innerHTML is replaced
   * Called after cart content is dynamically updated
   */
  reinitialize() {
    // Re-query internal elements after innerHTML replacement
    this.nextButton = this.querySelector('[data-next-step]');
    this.previousButton = this.querySelector('[data-previous-step]');
    this.dateInput = this.querySelector('#selected-date');
    this.timeSelect = this.querySelector('#selected-time');
    this.datePicker = this.querySelector('date-picker-component');

    // Re-attach event listeners to freshly-queried DOM nodes (remove first to prevent duplicates)
    if (this.nextButton) {
      this.nextButton.removeEventListener('click', this._boundHandleNextStep);
      this.nextButton.addEventListener('click', this._boundHandleNextStep);
    }
    if (this.previousButton) {
      this.previousButton.removeEventListener('click', this._boundHandlePreviousStep);
      this.previousButton.addEventListener('click', this._boundHandlePreviousStep);
    }

    // Location radio buttons
    const locationRadios = this.querySelectorAll('input[name="location"]');
    locationRadios.forEach(radio => {
      radio.removeEventListener('change', this._boundUpdateButtonState);
      radio.addEventListener('change', this._boundUpdateButtonState);
    });

    // Time select
    if (this.timeSelect) {
      this.timeSelect.removeEventListener('change', this._boundUpdateButtonState);
      this.timeSelect.addEventListener('change', this._boundUpdateButtonState);
    }

    // Date picker
    if (this.datePicker) {
      this.datePicker.addEventListener('dateselected', (event) => {
        this.handleDateSelected(event);
        this.updateButtonState();
      });
    }
  }

  // ==========================================
  // STATE MANAGEMENT
  // ==========================================

  /**
   * Navigate to a specific panel within Step 2
   */
  navigateToPanel(panel, options = {}) {
    const { buttonText = 'Next step', bookingConfirmed = false } = options;

    // Update state
    BookingStateManager.setState({
      step: BOOKING_STEPS.LOCATION,
      panel: panel,
      bookingConfirmed: bookingConfirmed
    });

    // Update DOM
    BookingStateManager.updateDOM();

    // Notify other components that booking state has changed
    document.dispatchEvent(new CustomEvent('booking:stateChanged'));

    // Update button
    if (this.nextButton) {
      this.nextButton.textContent = buttonText;
    }
    this.updateButtonState();

    // Update UI elements visibility
    this.updateUIVisibility(panel);
  }

  /**
   * Navigate to Step 3 (Cart Summary with booking details)
   */
  async navigateToCartSummary(bookingDetails) {
    const { locationName, locationImage, appointmentDate, appointmentTime, appointmentTimeText } = bookingDetails;

    // Save complete booking data
    BookingStateManager.setState({
      step: BOOKING_STEPS.CART_SUMMARY,
      panel: 1,
      appointmentDate,
      appointmentTime,
      appointmentTimeText,
      locationName,
      locationImage,
      bookingConfirmed: true
    });

    // Update DOM to show cart items
    BookingStateManager.updateDOM();

    // Notify other components that booking state has changed (for button visibility)
    document.dispatchEvent(new CustomEvent('booking:stateChanged'));

    // Format date for display
    const formattedDate = this.formatDateForDisplay(appointmentDate);

    // Show booking summary
    this.showBookingSummary(locationName, formattedDate, appointmentTimeText, locationImage);
    
    // Call saveBooking API when date/time is confirmed
    // Show loading overlay on cart drawer dialog while saving
    const cartDrawerDialog = document.querySelector('cart-drawer-component dialog.cart-drawer__dialog');
    const loadingOverlay = document.createElement('div');
    loadingOverlay.className = 'booking-save-loading-overlay';
    loadingOverlay.innerHTML = `
      <div class="booking-save-loading-spinner">
        <svg class="spinner" viewBox="0 0 50 50">
          <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5"></circle>
        </svg>
        <p>Processing your booking...</p>
      </div>
    `;
    if (cartDrawerDialog) {
      cartDrawerDialog.appendChild(loadingOverlay);
    } else {
      document.body.appendChild(loadingOverlay);
    }
    
    // Get finalise booking button reference to re-enable later if needed
    const finaliseBtn = document.querySelector('#finalise-booking-btn');
    
    try {
      const bookingJourney = document.querySelector('booking-journey');
      if (bookingJourney && typeof bookingJourney.saveBooking === 'function') {
        const result = await bookingJourney.saveBooking();
        
        // Save bookingId to cart attributes via CartBookingJourney component
        if (result?.bookingId) {
          const cartBookingJourney = document.querySelector('cart-booking-journey');
          if (cartBookingJourney && typeof cartBookingJourney.saveBookingToCartAttributes === 'function') {
            await cartBookingJourney.saveBookingToCartAttributes(result.bookingId);
          }
        }
      }
    } catch (error) {
      console.error('Error saving booking on date/time confirmation:', error);
    } finally {
      // Remove loading overlay
      loadingOverlay.remove();
      
      // Re-enable finalise booking button
      if (finaliseBtn) {
        finaliseBtn.disabled = false;
      }
    }
  }

  /**
   * Update UI elements based on current panel
   */
  updateUIVisibility(panel) {
    const bookingSummary = document.querySelector('#booking-summary');
    const bookingLocationBtn = document.querySelector('booking-location-btn');

    // Hide booking summary when in location/datetime panels
    if (bookingSummary) {
      bookingSummary.style.display = 'none';
    }

    // Show the "Choose Date & Time" button
    if (bookingLocationBtn) {
      bookingLocationBtn.style.display = 'flex';
    }
  }

  /**
   * Update button disabled state based on current panel and selections
   */
  updateButtonState() {
    if (!this.nextButton) return;

    const state = BookingStateManager.getState();
    const panel = state?.panel || 1;

    if (panel === 1) {
      // Panel 1: Enable if location is selected
      const selectedLocation = this.querySelector('input[name="location"]:checked');
      this.nextButton.disabled = !selectedLocation;
      this.previousButton.classList.remove('hidden');
    } else if (panel === 2) {
      // Panel 2: Enable if both date and time are selected
      const hasDate = this.dateInput?.value;
      const hasTime = this.timeSelect?.value;
      this.nextButton.disabled = !(hasDate && hasTime);
      this.previousButton.classList.remove('hidden');
    }
  }

  // ==========================================
  // EVENT HANDLERS
  // ==========================================

  /**
   * Handle "Choose Date & Time" button click from cart summary
   */
  async handleLocationButtonClick(event) {
    event.preventDefault();
    
    // Ensure the component is properly initialized
    this.initElements();
    
    // Always start at Panel 1 (location selection) when clicking from cart summary
    this.navigateToPanel(1, { buttonText: 'Next step' });

    // Show loading spinner and hide content
    this.showLocationLoading();

    // Always refresh location content from server to ensure it reflects current cart items
    // This is necessary because locations are filtered based on all items in cart
    const cartBookingJourney = document.querySelector('cart-booking-journey');
    if (cartBookingJourney && typeof cartBookingJourney.refreshLocationContent === 'function') {
      await cartBookingJourney.refreshLocationContent();
      // Re-initialize after content refresh
      this.initElements();
      this.reinitialize();
    }

    // Hide loading spinner
    this.hideLocationLoading();

    // Wait for DOM to be ready before loading location data
    // Use a retry mechanism to handle cases where content is dynamically rendered
    this.waitForLocationRadiosAndLoad();
  }

  /**
   * Show loading spinner while fetching location data
   */
  showLocationLoading() {
    const loading = this.querySelector('[data-locations-loading]');
    const content = this.querySelector('.step-panel-content[data-panel="1"]');
    if (loading) loading.hidden = false;
    if (content) content.style.display = 'none';
  }

  /**
   * Hide loading spinner after location data is fetched
   */
  hideLocationLoading() {
    const loading = this.querySelector('[data-locations-loading]');
    const content = this.querySelector('.step-panel-content[data-panel="1"]');
    if (loading) loading.hidden = true;
    if (content) content.style.removeProperty('display');
    
    // Show no-locations message if there are no location items
    const locationItems = this.querySelectorAll('.location-item');
    const noLocationsMsg = this.querySelector('.no-locations-message');
    if (noLocationsMsg && locationItems.length === 0) {
      noLocationsMsg.hidden = false;
    }
  }

  /**
   * Wait for location radio buttons to be available, then load selected location
   * Retries up to 10 times with increasing delays
   */
  waitForLocationRadiosAndLoad(attempt = 0) {
    const maxAttempts = 10;
    const delay = Math.min(100 * (attempt + 1), 500); // 100ms, 200ms, 300ms... up to 500ms

    const locationRadios = this.querySelectorAll('input[name="location"]');
    
    if (locationRadios.length > 0) {
      // Location radios are available, load the selected location
      this.loadSelectedLocation();
      this.updateButtonState();
      
      // Re-attach event listeners in case they were lost (must use same bound ref)
      locationRadios.forEach(radio => {
        radio.removeEventListener('change', this._boundUpdateButtonState);
        radio.addEventListener('change', this._boundUpdateButtonState);
      });
    } else if (attempt < maxAttempts) {
      // Radios not found yet, retry after delay
      setTimeout(() => {
        this.waitForLocationRadiosAndLoad(attempt + 1);
      }, delay);
    } else {
      console.warn('Location radios not found after maximum attempts');
    }
  }

  /**
   * Handle Next/Confirm button click
   */
  handleNextStep(event) {
    event.preventDefault();

    const state = BookingStateManager.getState();
    if (!state || state.step !== BOOKING_STEPS.LOCATION) return;

    if (state.panel === 1) {
      this.handleLocationConfirm();
    } else if (state.panel === 2) {
      this.handleDateTimeConfirm();
    }
  }

  /**
   * Handle Previous button click
   */
  handlePreviousStep(event) {
    event.preventDefault();

    const state = BookingStateManager.getState();
    if (!state || state.step !== BOOKING_STEPS.LOCATION) return;

    const currentPanel = parseInt(state.panel, 10) || 1;
    
    // If going back from Panel 2 (Date & Time), reset datepicker and clear errors
    if (currentPanel === 2) {
      // Reset the date picker
      if (this.datePicker && typeof this.datePicker.reset === 'function') {
        this.datePicker.reset();
      }
      
      // Clear the time select
      if (this.timeSelect) {
        this.timeSelect.value = '';
        this.timeSelect.innerHTML = '<option value="">Select a time</option>';
      }
      
      // Hide any error messages
      this.hideSlotsError();
    }
    
    // Panel 2 -> Panel 1, Panel 1 -> Panel 0 (cart view)
    const newPanel = currentPanel - 1;
    this.navigateToPanel(newPanel, { buttonText: 'Next step' });
  }

  /**
   * Handle location selection confirmation (Panel 1 -> Panel 2)
   */
  handleLocationConfirm() {
    const selectedLocation = this.querySelector('input[name="location"]:checked');
    if (!selectedLocation) return;

    // Save location ID
    BookingStateManager.setState({ locationId: selectedLocation.value });

    // Clone and display selected location in Panel 2
    this.displaySelectedLocation(selectedLocation);

    // Navigate to Panel 2
    this.navigateToPanel(2, { buttonText: 'Confirm' });

    // Disable button until date and time are selected
    if (this.nextButton) {
      this.nextButton.disabled = true;
    }
  }

  /**
   * Handle date/time confirmation (Panel 2 -> Step 3)
   */
  handleDateTimeConfirm() {
    const selectedDate = this.dateInput?.value;
    const selectedTime = this.timeSelect?.value;
    const selectedTimeText = this.timeSelect?.options[this.timeSelect.selectedIndex]?.text || '';

    if (!selectedDate || !selectedTime) return;

    // Get location details
    const state = BookingStateManager.getState();
    const locationId = state?.locationId;
    const locationItem = this.querySelector(`[data-location-id="${locationId}"]`);
    const locationName = locationItem?.querySelector('.location-name')?.textContent || locationId;
    const locationImage = locationItem?.querySelector('.location-image img')?.src || '';

    // Navigate to cart summary
    this.navigateToCartSummary({
      locationName,
      locationImage,
      appointmentDate: selectedDate,
      appointmentTime: selectedTime,
      appointmentTimeText: selectedTimeText
    });
  }

  // ==========================================
  // UI HELPERS
  // ==========================================

  /**
   * Display selected location in Panel 2
   */
  displaySelectedLocation(selectedRadio) {
    const selectedItem = selectedRadio.closest('.location-item');
    const displayContainer = this.querySelector('#selected-location-display');
    const locationNameEl = this.querySelector('#selected-location-name');

    if (selectedItem && displayContainer) {
      const clonedItem = selectedItem.cloneNode(true);
      const radioInput = clonedItem.querySelector('.location-radio');
      const locationName = clonedItem.querySelector('.location-name');
      
      // Remove radio input and location name from cloned item (name shown separately)
      if (radioInput) radioInput.remove();
      if (locationName) locationName.remove();
      
      displayContainer.innerHTML = '';
      displayContainer.appendChild(clonedItem);

      // Set the location name in the separate span
      if (locationNameEl) {
        const originalName = selectedItem.querySelector('.location-name');
        locationNameEl.textContent = originalName ? originalName.textContent : '';
      }
    }

    // Setup change button listeners for Panel 2
    this.setupChangeButtonListeners();
  }

  /**
   * Show booking summary in cart view
   */
  showBookingSummary(locationName, date, time, locationImage = '') {
    const bookingSummary = document.querySelector('#booking-summary');
    const locationEl = document.querySelector('#booking-summary-location');
    const datetimeEl = document.querySelector('#booking-summary-datetime');
    const locationImageEl = document.querySelector('#booking-summary-location-image');
    const applicantNameEl = document.querySelector('#booking-summary-applicant-name');
    const bookingLocationBtn = document.querySelector('booking-location-btn');

    // Show summary, hide location button
    if (bookingSummary) bookingSummary.style.display = 'flex';
    if (bookingLocationBtn) bookingLocationBtn.style.display = 'none';

    // Populate applicant name from state
    const state = BookingStateManager.getState();
    if (applicantNameEl && state?.applicantName) {
      applicantNameEl.textContent = state.applicantName.toLowerCase();
    }

    // Populate values
    if (locationEl) locationEl.textContent = locationName;
    if (datetimeEl) datetimeEl.textContent = `${date}, ${time}`;
    if (locationImageEl && locationImage) {
      locationImageEl.src = locationImage;
      locationImageEl.alt = locationName;
      locationImageEl.style.display = 'block';
    }

    // Add change button listeners (only once)
    this.setupChangeButtonListeners();
  }

  /**
   * Setup change button listeners using event delegation
   * Uses delegation on cart-drawer-component to handle dynamically added buttons
   */
  setupChangeButtonListeners() {
    if (CartBookingLocation.changeListenersAdded) return;

    // Use event delegation on the cart drawer component (doesn't get replaced)
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer) {
      cartDrawer.addEventListener('click', (e) => {
        const target = e.target;
        
        // Handle change location button
        if (target.matches('[data-change-location]') || target.closest('[data-change-location]')) {
          e.preventDefault();
          this.resetTimeSlots();
          this.navigateToPanel(1, { buttonText: 'Next step' });
        }
        
        // Handle change datetime button
        if (target.matches('[data-change-datetime]') || target.closest('[data-change-datetime]')) {
          e.preventDefault();
          this.navigateToPanel(2, { buttonText: 'Confirm' });
        }
      });
    }

    CartBookingLocation.changeListenersAdded = true;
  }

  /**
   * Restore booking state on drawer open
   */
  restoreBookingState() {
    if (!BookingStateManager.isBookingConfirmed()) return;

    const state = BookingStateManager.getState();

    // Update DOM
    BookingStateManager.updateDOM();

    // Format date and show summary
    const formattedDate = this.formatDateForDisplay(state.appointmentDate);
    this.showBookingSummary(
      state.locationName || state.locationId,
      formattedDate,
      state.appointmentTimeText || '',
      state.locationImage || ''
    );
  }

  /**
   * Load selected location from state (for restoring on drawer open)
   */
  loadSelectedLocation() {
    const state = BookingStateManager.getState();
    if (!state?.locationId) return;

    // Select the saved radio button
    const savedRadio = this.querySelector(`input[name="location"][value="${state.locationId}"]`);
    if (savedRadio) {
      savedRadio.checked = true;
      // Update button state after selecting the location
      this.updateButtonState();
    }

    // If on Panel 2, restore the display
    if (state.panel >= 2 && state.step === BOOKING_STEPS.LOCATION) {
      this.displaySelectedLocation(savedRadio);
    }
  }

  // ==========================================
  // DATE/TIME HELPERS
  // ==========================================

  formatDateForDisplay(dateString) {
    const dateObj = new Date(dateString);
    return dateObj.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  formatAppointmentDate(dateString) {
    const selectedDate = new Date(dateString);
    const today = new Date();
    const isToday = selectedDate.toDateString() === today.toDateString();

    if (isToday) {
      return today.toISOString();
    } else {
      selectedDate.setHours(12, 6, 7, 192);
      return selectedDate.toISOString();
    }
  }

  handleDateSelected(event) {
    const { value } = event.detail;
    if (!value) return;

    const state = BookingStateManager.getState();
    const locationId = state?.locationId;
    if (!locationId) {
      console.error('No location selected');
      return;
    }

    this.showTimeLoading();

    const appointmentDate = this.formatAppointmentDate(value);
    const serviceCodes = this.getServiceCodes();

    const bookingJourneyComponent = document.querySelector('booking-journey');
    if (bookingJourneyComponent) {
      // Hide any previous error message
      this.hideSlotsError();
      
      bookingJourneyComponent.bookingSlots(appointmentDate, locationId, serviceCodes)
        .then((result) => {
          this.hideTimeLoading();
          if (result?.slots && result.slots.length > 0) {
            this.populateTimeSlots(result.slots);
          } else {
            // No slots available
            this.showSlotsError('No available time slots for this date. Please try another date.');
          }
        })
        .catch((error) => {
          this.hideTimeLoading();
          console.error('Failed to fetch booking slots:', error);
          this.showSlotsError(Theme.translations?.booking?.slots_error || 'Something went wrong. Please try again.');
        });
    }
  }
  
  /**
   * Show error message for slots fetch
   */
  showSlotsError(message) {
    const errorEl = document.getElementById('slots-error-message');
    if (errorEl) {
      errorEl.innerHTML = message;
      errorEl.style.display = 'block';
    }
    // Reset time select to disabled state
    if (this.timeSelect) {
      this.timeSelect.innerHTML = `<option value="" disabled selected>${Theme.translations?.booking?.select_date_first || 'Select a date first'}</option>`;
      this.timeSelect.disabled = true;
    }
  }
  
  /**
   * Hide error message for slots fetch
   */
  hideSlotsError() {
    const errorEl = document.getElementById('slots-error-message');
    if (errorEl) {
      errorEl.style.display = 'none';
    }
  }

  getServiceCodes() {
    const serviceCodesElement = this.querySelector('[data-service-codes]');
    const codesString = serviceCodesElement?.getAttribute('data-service-codes') || '';
    const codes = codesString.split(',').map(code => code.trim()).filter(Boolean);

    // data-service-codes is built in Liquid from the cart's line items, so switching the
    // service fee off removes the only item carrying 'SF' and would silently drop it
    // from the availability query. Apply the same rule saveBooking() uses, so the slots
    // lookup and the saved booking always agree on the service types.
    if (shouldSendServiceFeeCode() && !codes.includes('SF')) {
      codes.push('SF');
    }

    return codes;
  }

  populateTimeSlots(slots) {
    if (!this.timeSelect) return;

    this.timeSelect.innerHTML = `<option value="" disabled selected>${Theme.translations?.booking?.select_available_time || 'Select an available time'}</option>`;

    slots.forEach((slot) => {
      const option = document.createElement('option');
      option.value = slot.slotId;
      option.textContent = slot.slot;
      this.timeSelect.appendChild(option);
    });

    this.timeSelect.disabled = false;
  }

  showTimeLoading() {
    const spinner = this.querySelector('#time-loading-spinner');
    if (spinner) spinner.style.display = 'block';

    if (this.timeSelect) {
      this.timeSelect.disabled = true;
      this.timeSelect.innerHTML = `<option value="" disabled selected>${Theme.translations?.booking?.loading_times || 'Loading times...'}</option>`;
    }
  }

  hideTimeLoading() {
    const spinner = this.querySelector('#time-loading-spinner');
    if (spinner) spinner.style.display = 'none';
  }

  /**
   * Reset time slots to initial state (used when changing location)
   */
  resetTimeSlots() {
    if (this.timeSelect) {
      this.timeSelect.innerHTML = `<option value="" disabled selected>${Theme.translations?.cart?.select_date_first || 'Select a date first'}</option>`;
      this.timeSelect.disabled = true;
    }
    
    // Clear the date input
    if (this.dateInput) {
      this.dateInput.value = '';
    }
    
    // Reset date picker component
    if (this.datePicker) {
      // Clear the hidden input
      const dateInput = this.datePicker.querySelector('[data-date-input]');
      if (dateInput) {
        dateInput.value = '';
      }
      
      // Reset the display text
      const displayEl = this.datePicker.querySelector('[data-selected-date-display]');
      if (displayEl) {
        displayEl.textContent = this.datePicker.dataset.placeholder || 'Select a date';
      }
      
      // Clear selected date state
      this.datePicker.selectedDate = null;
    }
  }
}

if (!customElements.get('cart-booking-location')) {
  customElements.define('cart-booking-location', CartBookingLocation);
}